/** Format source measurements without mutating their canonical centimeter values. */
export function formatMeasurement(centimeters, unit = 'cm') {
  const value = Number(centimeters);
  if (!Number.isFinite(value)) return '';
  const converted = unit === 'in' ? value / 2.54 : value;
  return new Intl.NumberFormat(typeof document === 'undefined' ? 'fr-CH' : (document.documentElement.lang === 'en' ? 'en-GB' : 'fr-CH'), {
    minimumFractionDigits: unit === 'in' ? 1 : 0,
    maximumFractionDigits: 1,
  }).format(converted);
}

/** A single mutation attempt: callers must never retry an uncertain response automatically. */
export async function addProductToCart(url, variantId, fetcher = fetch) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetcher(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      signal: controller.signal,
      body: JSON.stringify({ items: [{ id: String(variantId), quantity: 1 }] }),
    });
    const payload = await response.json();
    if (!response.ok) {
      const error = new Error('Cart addition rejected');
      error.status = response.status;
      error.userMessage = typeof payload.description === 'string' ? payload.description : null;
      throw error;
    }
    if (!Array.isArray(payload.items) || !payload.items.some((item) => String(item.variant_id || item.id) === String(variantId))) {
      throw new Error('Unexpected cart response');
    }
    return payload;
  } finally {
    clearTimeout(timeout);
  }
}

if (typeof customElements !== 'undefined') {
  class LlukesSizeGuide extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const controls = this.querySelector('[data-unit-controls]');
      if (controls) controls.hidden = false;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-measurement-unit]');
        if (!button) return;
        const unit = button.dataset.measurementUnit;
        this.querySelectorAll('[data-measurement-cm]').forEach((cell) => {
          cell.textContent = formatMeasurement(cell.dataset.measurementCm, unit);
        });
        this.querySelectorAll('[data-measurement-unit]').forEach((control) => {
          control.setAttribute('aria-pressed', String(control === button));
        });
        this.querySelector('[data-active-unit]').textContent = unit === 'in' ? window.llukesStrings.inches : window.llukesStrings.centimetres;
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class LlukesProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.pending = false;
      this.zoomIndex = 0;
      const signal = this.controller.signal;
      this.form = this.querySelector('[data-product-form]');
      this.addButton = this.querySelector('[data-product-add]');
      this.defaultButtonText = this.addButton.textContent.trim();
      this.error = this.querySelector('[data-product-error]');
      this.cartCheck = this.querySelector('[data-cart-check]');
      this.gallery = this.querySelector('[data-product-gallery]');
      this.images = [...this.querySelectorAll('[data-gallery-image]')];
      this.zoom = this.querySelector('[data-product-zoom]');
      this.zoomBody = this.querySelector('.llukes-product__zoom-body');
      this.zoomImage = this.querySelector('.llukes-product__zoom-image');
      this.zoomScale = 1;
      this.zoomed = false;
      this.zoomPreviewCache = new Map();
      this.zoomFullCache = new Map();
      this.zoomImageRequestId = 0;
      this.zoomWheelConfig = {
        axisLockDistance: 8,
        horizontalIntentRatio: 1.2,
        triggerDistance: 50,
        silenceRearmDelay: 80,
        inversionRearmDistance: 12,
        resumedGestureMinimumDelta: 8,
        resumedGestureInertiaMultiplier: 1.8,
        navigationLockDuration: 260,
      };
      this.zoomWheelLockedUntil = 0;
      this.resetZoomWheelState();
      if (this.zoomImage) {
        this.zoomImage.tabIndex = 0;
        this.zoomImage.setAttribute('role', 'button');
        this.zoomImage.setAttribute('aria-pressed', 'false');
        this.zoomImage.setAttribute('aria-label', this.zoom?.dataset.zoomInLabel || 'Zoom in');
      }
      this.mobileSummary = this.querySelector('[data-mobile-product-summary]');
      this.galleryProgress = this.querySelector('[data-gallery-progress]');
      this.galleryVisible = false;
      // Browsers can restore radio state on reload; a new consultation starts unselected.
      this.form.querySelectorAll('input[name="id"]').forEach((input) => { input.checked = false; });
      this.form.addEventListener('submit', (event) => this.submit(event), { signal });
      this.form.addEventListener('change', (event) => {
        if (!event.target.matches('input[name="id"]')) return;
        this.hideError();
        this.querySelector('[data-product-price]').textContent = event.target.dataset.variantPrice;
      }, { signal });
      this.form.addEventListener('invalid', () => {
        this.showError(window.llukesStrings.select_size);
      }, { capture: true, signal });
      this.addButton.addEventListener('click', (event) => {
        if (event.detail) this.addButton.classList.add('is-hover-consumed');
      }, { signal });
      this.addButton.addEventListener('pointerleave', () => {
        this.addButton.classList.remove('is-hover-consumed');
      }, { signal });
      this.addEventListener('click', (event) => {
        const target = event.target.closest('button');
        if (!target) return;
        if (target.matches('[data-zoom-index]')) this.setZoom(Number(target.dataset.zoomIndex));
        if (target.matches('[data-zoom-previous]')) this.setZoom(this.zoomIndex - 1, 'previous');
        if (target.matches('[data-zoom-next]')) this.setZoom(this.zoomIndex + 1, 'next');
        if (target.matches('[data-scroll-to-sizes]')) this.scrollToSizes();
      }, { signal });
      window.addEventListener('scroll', () => {
        cancelAnimationFrame(this.galleryFrame);
        this.galleryFrame = requestAnimationFrame(() => this.updateGalleryProgress());
      }, { passive: true, signal });
      this.zoom?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.setZoom(this.zoomIndex + (event.key === 'ArrowLeft' ? -1 : 1), event.key === 'ArrowLeft' ? 'previous' : 'next');
        }
      }, { signal });
      this.zoomBody?.addEventListener('touchstart', (event) => this.startZoomGesture(event), { passive: false, signal });
      this.zoomBody?.addEventListener('touchmove', (event) => this.moveZoomGesture(event), { passive: false, signal });
      this.zoomBody?.addEventListener('touchend', (event) => this.finishZoomGesture(event), { passive: false, signal });
      this.zoomBody?.addEventListener('touchcancel', () => this.cancelZoomGesture(), { passive: true, signal });
      this.zoomBody?.addEventListener('wheel', (event) => this.handleZoomWheel(event), { passive: false, signal });
      this.zoomImage?.addEventListener('click', (event) => {
        if (performance.now() < this.zoomSuppressClickUntil) return;
        this.toggleZoomScale({ x: event.clientX, y: event.clientY });
      }, { signal });
      this.zoomImage?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.toggleZoomScale();
      }, { signal });
      this.zoom?.addEventListener('close', () => this.resetZoomScale(), { signal });
      this.summaryObserver = new IntersectionObserver(([entry]) => {
        this.galleryVisible = entry.isIntersecting;
        this.updateMobileSummary();
        this.updateGalleryProgress();
      }, { threshold: 0.01 });
      this.summaryObserver.observe(this.gallery);
      window.addEventListener('resize', () => {
        this.updateMobileSummary();
        this.updateGalleryProgress();
      }, { passive: true, signal });
      document.addEventListener('llukes:overlay', (event) => {
        this.overlayOpen = Boolean(event.detail?.open);
        this.updateMobileSummary();
      }, { signal });
      this.updateMobileSummary();
      this.updateGalleryProgress();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.summaryObserver?.disconnect();
      cancelAnimationFrame(this.galleryFrame);
      cancelAnimationFrame(this.zoomScaleFrame);
    }
    hideError() { this.error.hidden = true; this.error.textContent = ''; this.cartCheck.hidden = true; }
    showError(message, uncertain = false) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.cartCheck.hidden = !uncertain;
    }
    async submit(event) {
      event.preventDefault();
      if (this.pending) return;
      const selection = this.form.querySelector('input[name="id"]:checked:not(:disabled)');
      if (!selection) {
        this.showError(window.llukesStrings.select_size);
        this.form.querySelector('input[name="id"]:not(:disabled)')?.focus();
        return;
      }
      this.pending = true;
      this.hideError();
      this.addButton.disabled = true;
      this.addButton.textContent = window.llukesStrings.adding;
      this.form.setAttribute('aria-busy', 'true');
      try {
        await addProductToCart(this.form.dataset.addUrl, selection.value);
        document.dispatchEvent(new CustomEvent('llukes:cart-updated', { detail: { open: true } }));
      } catch (error) {
        if (error.status === 422) {
          this.showError(window.llukesStrings.stock_error);
        } else if (error.status && error.status < 500) {
          this.showError(window.llukesStrings.add_refused);
        } else {
          this.showError(window.llukesStrings.add_uncertain, true);
        }
      } finally {
        this.pending = false;
        this.addButton.disabled = false;
        this.addButton.textContent = this.defaultButtonText;
        this.form.removeAttribute('aria-busy');
      }
    }
    updateMobileSummary() {
      const mobile = !window.matchMedia('(min-width: 990px)').matches;
      const hidden = !mobile || !this.galleryVisible || this.overlayOpen;
      if (this.mobileSummary) this.mobileSummary.hidden = hidden;
      if (this.galleryProgress) this.galleryProgress.hidden = hidden;
    }
    updateGalleryProgress() {
      if (!this.galleryProgress || !this.images.length || !this.galleryVisible || window.matchMedia('(min-width: 990px)').matches) return;
      const viewportCenter = window.innerHeight / 2;
      let activeIndex = 0;
      let closestDistance = Infinity;
      this.images.forEach((image, index) => {
        const rect = image.getBoundingClientRect();
        const distance = Math.abs((rect.top + (rect.height / 2)) - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });
      const label = (window.llukesStrings?.photo_count || 'Photo {{ index }} of {{ total }}')
        .replace('{{ index }}', String(activeIndex + 1))
        .replace('{{ total }}', String(this.images.length));
      this.galleryProgress.setAttribute('aria-label', label);
      this.galleryProgress.querySelectorAll('[data-gallery-dot]').forEach((dot, index) => {
        if (index === activeIndex) dot.dataset.active = 'true';
        else delete dot.dataset.active;
      });
    }
    scrollToSizes() {
      const firstSize = this.form.querySelector('input[name="id"]:not(:disabled)');
      if (!firstSize) return;
      firstSize.focus({ preventScroll: true });
      this.querySelector('.llukes-product__sizes')?.scrollIntoView({
        block: 'center',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }
    zoomPoint(touch) { return { x: touch.clientX, y: touch.clientY }; }
    zoomDistance(first, second) { return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY); }
    zoomMidpoint(first, second) { return { x: (first.clientX + second.clientX) / 2, y: (first.clientY + second.clientY) / 2 }; }
    startZoomGesture(event) {
      this.zoomSuppressClickUntil = performance.now() + 500;
      if (event.touches.length >= 2) {
        event.preventDefault();
        const [first, second] = event.touches;
        this.zoomGesture = {
          mode: 'pinch',
          distance: Math.max(1, this.zoomDistance(first, second)),
          scale: this.zoomScale,
          moved: true,
        };
        return;
      }
      const touch = event.touches[0];
      if (!touch) return;
      this.zoomGesture = {
        mode: this.zoomScale > 1 ? 'pan' : 'swipe',
        start: this.zoomPoint(touch),
        last: this.zoomPoint(touch),
        scrollLeft: this.zoomBody?.scrollLeft || 0,
        scrollTop: this.zoomBody?.scrollTop || 0,
        moved: false,
      };
    }
    moveZoomGesture(event) {
      if (event.touches.length >= 2) {
        event.preventDefault();
        const [first, second] = event.touches;
        if (this.zoomGesture?.mode !== 'pinch') {
          this.zoomGesture = {
            mode: 'pinch',
            distance: Math.max(1, this.zoomDistance(first, second)),
            scale: this.zoomScale,
            moved: true,
          };
        }
        const scale = this.zoomGesture.scale * (this.zoomDistance(first, second) / this.zoomGesture.distance);
        this.setZoomScale(scale, this.zoomMidpoint(first, second));
        return;
      }
      const touch = event.touches[0];
      const gesture = this.zoomGesture;
      if (!touch || !gesture || gesture.mode === 'pinch') return;
      const point = this.zoomPoint(touch);
      const distanceX = point.x - gesture.start.x;
      const distanceY = point.y - gesture.start.y;
      gesture.last = point;
      gesture.moved ||= Math.hypot(distanceX, distanceY) > 8;
      if (gesture.mode === 'pan' || this.zoomScale > 1) {
        event.preventDefault();
        this.zoomBody?.scrollTo({ left: gesture.scrollLeft - distanceX, top: gesture.scrollTop - distanceY });
      } else if (Math.abs(distanceX) > Math.abs(distanceY)) {
        event.preventDefault();
      }
    }
    finishZoomGesture(event) {
      const gesture = this.zoomGesture;
      this.zoomSuppressClickUntil = performance.now() + 500;
      if (!gesture) return;
      if (gesture.mode === 'pinch') {
        if (event.touches.length === 1) {
          const point = this.zoomPoint(event.touches[0]);
          this.zoomGesture = {
            mode: this.zoomScale > 1 ? 'pan' : 'swipe',
            start: point,
            last: point,
            scrollLeft: this.zoomBody?.scrollLeft || 0,
            scrollTop: this.zoomBody?.scrollTop || 0,
            moved: true,
          };
        } else {
          this.zoomGesture = null;
        }
        return;
      }
      if (event.touches.length) return;
      this.zoomGesture = null;
      const touch = event.changedTouches[0];
      const end = touch ? this.zoomPoint(touch) : gesture.last;
      const distanceX = end.x - gesture.start.x;
      const distanceY = end.y - gesture.start.y;
      if (!gesture.moved) {
        this.registerZoomTap(end);
        return;
      }
      this.zoomLastTap = null;
      if (gesture.mode !== 'swipe' || this.zoomScale > 1 || Math.abs(distanceX) < 50 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
      this.setZoom(this.zoomIndex + (distanceX < 0 ? 1 : -1), distanceX < 0 ? 'next' : 'previous');
    }
    cancelZoomGesture() {
      this.zoomGesture = null;
      this.zoomLastTap = null;
      this.zoomSuppressClickUntil = performance.now() + 500;
    }
    registerZoomTap(point) {
      const now = performance.now();
      const previous = this.zoomLastTap;
      this.zoomLastTap = { ...point, time: now };
      if (!previous || now - previous.time > 300 || Math.hypot(point.x - previous.x, point.y - previous.y) > 30) return;
      this.zoomLastTap = null;
      this.toggleZoomScale(point);
    }
    resetZoomWheelState() {
      this.zoomWheelState = {
        phase: 'idle',
        axis: null,
        horizontalDistance: 0,
        verticalDistance: 0,
        horizontalDelta: 0,
        direction: 0,
        inversionDistance: 0,
        lastInertiaDelta: 0,
        lastEventAt: 0,
      };
    }
    startZoomWheelGesture(distanceX, distanceY) {
      const state = this.zoomWheelState;
      const config = this.zoomWheelConfig;
      state.horizontalDistance += Math.abs(distanceX);
      state.verticalDistance += Math.abs(distanceY);
      state.horizontalDelta += distanceX;
      if (!state.axis && state.horizontalDistance + state.verticalDistance >= config.axisLockDistance) {
        if (state.horizontalDistance >= state.verticalDistance * config.horizontalIntentRatio) state.axis = 'horizontal';
        else if (state.verticalDistance >= state.horizontalDistance * config.horizontalIntentRatio) state.axis = 'vertical';
      }
      if (state.axis !== 'horizontal' || Math.abs(state.horizontalDelta) < config.triggerDistance) return 0;
      state.phase = 'absorbing';
      state.direction = state.horizontalDelta > 0 ? 1 : -1;
      state.inversionDistance = 0;
      state.lastInertiaDelta = Math.abs(distanceX);
      return state.direction;
    }
    absorbZoomWheelDelta(distanceX, distanceY, canRearm = true) {
      const state = this.zoomWheelState;
      const config = this.zoomWheelConfig;
      const magnitude = Math.abs(distanceX);
      if (!magnitude) return 0;
      const direction = distanceX > 0 ? 1 : -1;
      if (direction !== state.direction) {
        state.inversionDistance += magnitude;
        if (!canRearm || state.inversionDistance < config.inversionRearmDistance) return 0;
        const inversionDistance = state.inversionDistance;
        this.resetZoomWheelState();
        this.zoomWheelState.axis = 'horizontal';
        this.zoomWheelState.horizontalDistance = inversionDistance;
        this.zoomWheelState.horizontalDelta = direction * inversionDistance;
        return this.startZoomWheelGesture(0, distanceY);
      }
      state.inversionDistance = 0;
      if (canRearm && magnitude >= config.resumedGestureMinimumDelta && magnitude >= state.lastInertiaDelta * config.resumedGestureInertiaMultiplier) {
        this.resetZoomWheelState();
        return this.startZoomWheelGesture(distanceX, distanceY);
      }
      state.lastInertiaDelta = magnitude;
      return 0;
    }
    handleZoomWheel(event) {
      if (this.zoomScale > 1 || !this.zoomBody || event.ctrlKey) return;
      const multiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.zoomBody.clientWidth : 1;
      const distanceX = event.deltaX * multiplier;
      const distanceY = event.deltaY * multiplier;
      if (Math.abs(distanceX) < 1 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
      const now = performance.now();
      const locked = now < this.zoomWheelLockedUntil;
      if (!locked && this.zoomWheelState.lastEventAt && now - this.zoomWheelState.lastEventAt >= this.zoomWheelConfig.silenceRearmDelay) this.resetZoomWheelState();
      if (locked) {
        if (this.zoomWheelState.phase === 'absorbing') {
          event.preventDefault();
          this.absorbZoomWheelDelta(distanceX, distanceY, false);
          this.zoomWheelState.lastEventAt = now;
        }
        return;
      }
      const direction = this.zoomWheelState.phase === 'absorbing'
        ? this.absorbZoomWheelDelta(distanceX, distanceY)
        : this.startZoomWheelGesture(distanceX, distanceY);
      this.zoomWheelState.lastEventAt = now;
      if (this.zoomWheelState.axis !== 'horizontal' && this.zoomWheelState.phase !== 'absorbing') return;
      event.preventDefault();
      if (!direction) return;
      this.zoomWheelLockedUntil = now + this.zoomWheelConfig.navigationLockDuration;
      this.setZoom(this.zoomIndex + direction, direction > 0 ? 'next' : 'previous', true);
    }
    setZoom(index, direction, preserveWheelState = false) {
      if (!this.images.length || !this.zoomImage) return;
      this.resetZoomScale();
      if (!preserveWheelState) {
        this.resetZoomWheelState();
        this.zoomWheelLockedUntil = 0;
      }
      this.zoomIndex = (index + this.images.length) % this.images.length;
      const source = this.images[this.zoomIndex];
      const galleryImage = source.querySelector('img');
      const preview = galleryImage?.complete && galleryImage.naturalWidth > 0
        ? galleryImage.currentSrc
        : source.dataset.zoomPreviewSrc;
      const requestId = ++this.zoomImageRequestId;
      this.zoomImage.removeAttribute('srcset');
      this.zoomImage.src = preview || source.dataset.zoomSrc;
      this.zoomImage.alt = source.dataset.zoomAlt;
      this.primeZoomPreviews();
      this.loadFullZoomImage(source.dataset.zoomSrc, requestId);
      if (direction && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const animation = direction === 'next' ? 'is-entering-from-right' : 'is-entering-from-left';
        this.zoomImage.classList.remove('is-entering-from-right', 'is-entering-from-left');
        void this.zoomImage.offsetWidth;
        this.zoomImage.classList.add(animation);
      }
      this.querySelector('.llukes-product__zoom-body')?.scrollTo({ top: 0 });
      const counter = this.querySelector('[data-zoom-counter]');
      if (counter) counter.textContent = `${this.zoomIndex + 1} / ${this.images.length}`;
    }
    primeZoomPreviews() {
      for (const source of this.images) {
        const url = source.dataset.zoomPreviewSrc;
        if (!url || this.zoomPreviewCache.has(url)) continue;
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        this.zoomPreviewCache.set(url, image);
      }
    }
    loadFullZoomImage(url, requestId) {
      if (!url) return;
      let ready = this.zoomFullCache.get(url);
      if (!ready) {
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        ready = image.decode().then(() => url, () => null);
        this.zoomFullCache.set(url, ready);
      }
      ready.then((loadedUrl) => {
        if (loadedUrl && this.zoomImageRequestId === requestId) this.zoomImage.src = loadedUrl;
      });
    }
    toggleZoomScale(focalPoint) {
      this.setZoomScale(this.zoomScale > 1 ? 1 : 1.75, focalPoint);
    }
    setZoomScale(scale, focalPoint) {
      if (!this.zoom || !this.zoomImage) return;
      const nextScale = Math.min(3, Math.max(1, Number(scale) || 1));
      const previousScale = this.zoomScale;
      if (previousScale === 1 && nextScale > 1) {
        const displayedSize = this.zoomImage.getBoundingClientRect();
        if (displayedSize.width > 0 && displayedSize.height > 0) {
          this.zoomBaseSize = { width: displayedSize.width, height: displayedSize.height };
        }
      }
      if (nextScale > 1 && !this.zoomBaseSize) return;
      const imageRect = this.zoomImage.getBoundingClientRect();
      const bodyRect = this.zoomBody?.getBoundingClientRect();
      const focus = focalPoint && bodyRect && focalPoint.x >= bodyRect.left && focalPoint.x <= bodyRect.right && focalPoint.y >= bodyRect.top && focalPoint.y <= bodyRect.bottom
        ? focalPoint
        : bodyRect ? { x: bodyRect.left + bodyRect.width / 2, y: bodyRect.top + bodyRect.height / 2 } : { x: 0, y: 0 };
      const imageX = imageRect.width ? Math.min(1, Math.max(0, (focus.x - imageRect.left) / imageRect.width)) : 0.5;
      const imageY = imageRect.height ? Math.min(1, Math.max(0, (focus.y - imageRect.top) / imageRect.height)) : 0.5;
      this.zoomScale = nextScale;
      this.zoomed = nextScale > 1;
      this.zoom.classList.toggle('is-zoomed', this.zoomed);
      this.zoomImage.setAttribute('aria-pressed', String(this.zoomed));
      this.zoomImage.setAttribute('aria-label', this.zoomed ? this.zoom.dataset.zoomOutLabel : this.zoom.dataset.zoomInLabel);
      if (this.zoomed) {
        this.zoomImage.style.setProperty('--llukes-zoom-width', `${this.zoomBaseSize.width * nextScale}px`);
        this.zoomImage.style.setProperty('--llukes-zoom-height', `${this.zoomBaseSize.height * nextScale}px`);
        cancelAnimationFrame(this.zoomScaleFrame);
        this.zoomScaleFrame = requestAnimationFrame(() => {
          const nextImageRect = this.zoomImage.getBoundingClientRect();
          this.zoomBody?.scrollTo({
            left: Math.max(0, (this.zoomBody.scrollLeft || 0) + nextImageRect.left + (nextImageRect.width * imageX) - focus.x),
            top: Math.max(0, (this.zoomBody.scrollTop || 0) + nextImageRect.top + (nextImageRect.height * imageY) - focus.y),
          });
        });
      } else {
        cancelAnimationFrame(this.zoomScaleFrame);
        this.zoomImage.style.removeProperty('--llukes-zoom-width');
        this.zoomImage.style.removeProperty('--llukes-zoom-height');
        this.zoomBody?.scrollTo({ left: 0, top: 0 });
        this.zoomBaseSize = null;
      }
    }
    resetZoomScale() {
      this.setZoomScale(1);
      this.zoomGesture = null;
      this.zoomLastTap = null;
    }
  }
  if (!customElements.get('llukes-size-guide')) customElements.define('llukes-size-guide', LlukesSizeGuide);
  if (!customElements.get('llukes-product')) customElements.define('llukes-product', LlukesProduct);
}
